import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  Check,
  Clock,
  FileText,
  Layers,
  MapPin,
  Package,
  Pencil,
  Phone,
  Star,
  Trash2,
  Truck,
  Wallet,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CompactStatCard, PageHeader, StatsGrid, StandardImage } from "@/components/shared";
import {
  useAdminStoreDetail,
  useApproveStoreMutation,
  useDeleteAdminStoreMutation,
  useSuspendStoreMutation,
} from "@/hooks/queries";
import { resolveUploadUrl } from "@/services/filesService";
import { mapStoreStatusToUi } from "@/lib/mappers/admin-mappers";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { AdminAdjustStoreBvDialog } from "@/components/admin/AdminAdjustStoreBvDialog";

const statusBadge: Record<string, string> = {
  approved: "bg-success/10 text-success border-success/20",
  pending: "bg-warning/10 text-warning border-warning/20",
  suspended: "bg-destructive/10 text-destructive border-destructive/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatTime(time?: string) {
  if (!time) return "—";
  return time.slice(0, 5);
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-4 py-2 border-b border-border/60 last:border-0">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right sm:text-left break-all">{value}</span>
    </div>
  );
}

const AdminStoreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const storeId = id ? Number(id) : 0;
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [bvOpen, setBvOpen] = useState(false);

  const { data, isLoading, refetch } = useAdminStoreDetail(storeId);
  const approveStore = useApproveStoreMutation();
  const suspendStore = useSuspendStoreMutation();
  const deleteStore = useDeleteAdminStoreMutation();

  const store = data?.store;
  const dashboard = data?.dashboard;
  const products = data?.products;

  const uiStatus = store ? mapStoreStatusToUi(store.status) : "pending";

  const handleApprove = async () => {
    try {
      await approveStore.mutateAsync({ id: storeId, approve: true });
      toast({ title: "Store approved" });
      refetch();
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleReject = async () => {
    try {
      await approveStore.mutateAsync({ id: storeId, approve: false, rejectionReason: "Rejected by admin" });
      toast({ title: "Store rejected", variant: "destructive" });
      refetch();
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleSuspend = async () => {
    try {
      await suspendStore.mutateAsync({ id: storeId, reason: "Suspended by admin" });
      toast({ title: "Store suspended", variant: "destructive" });
      refetch();
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteStore.mutateAsync(storeId);
      toast({ title: "Store deleted" });
      navigate("/admin/stores");
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 rounded-xl" />
        <div className="grid gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Store not found.</p>
        <Button variant="outline" asChild>
          <Link to="/admin/stores">Back to stores</Link>
        </Button>
      </div>
    );
  }

  const bannerUrl = store.bannerUrl ? resolveUploadUrl(store.bannerUrl) : undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/stores">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <PageHeader title="Store details" description="Vendor profile and operational overview" className="flex-1 min-w-0" />
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => setBvOpen(true)}>
            <Layers className="h-4 w-4" /> Add BV
          </Button>
          <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate("/admin/stores", { state: { editStoreId: storeId } })}>
            <Pencil className="h-4 w-4" /> Edit
          </Button>
          {uiStatus === "pending" && (
            <>
              <Button size="sm" className="gap-1 bg-success hover:bg-success/90" onClick={handleApprove}>
                <Check className="h-4 w-4" /> Approve
              </Button>
              <Button size="sm" variant="destructive" className="gap-1" onClick={handleReject}>
                <Ban className="h-4 w-4" /> Reject
              </Button>
            </>
          )}
          {uiStatus === "approved" && (
            <Button size="sm" variant="destructive" className="gap-1" onClick={handleSuspend}>
              <Ban className="h-4 w-4" /> Suspend
            </Button>
          )}
          {uiStatus === "suspended" && (
            <Button size="sm" className="gap-1" onClick={handleApprove}>
              <Check className="h-4 w-4" /> Reactivate
            </Button>
          )}
          {uiStatus === "rejected" && (
            <Button size="sm" className="gap-1 bg-success hover:bg-success/90" onClick={handleApprove}>
              <Check className="h-4 w-4" /> Approve
            </Button>
          )}
          <Button size="sm" variant="outline" className="gap-1 text-destructive" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="relative h-32 sm:h-40 bg-gradient-to-r from-primary/20 to-primary/5">
          {bannerUrl && (
            <img src={bannerUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-90" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
        </div>
        <CardContent className="relative px-6 pb-6 pt-0">
          <div className="-mt-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-end gap-4">
              <StandardImage
                src={store.logoUrl}
                alt={store.storeName}
                standard="storeLogo"
                className="h-20 w-20 border-2 border-background shadow-md"
                rounded="xl"
              />
              <div className="pb-1">
                <h1 className="text-2xl font-heading font-bold">{store.storeName}</h1>
                <p className="text-sm text-muted-foreground">
                  {store.city}, {store.state} · Joined {formatDate(store.createdAt)}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Badge variant="outline" className={statusBadge[uiStatus]}>{store.statusText}</Badge>
                  <Badge variant="outline">{store.isOpen ? "Open now" : "Closed"}</Badge>
                  {store.kycVerified && (
                    <Badge variant="outline" className="bg-success/10 text-success border-success/20">KYC verified</Badge>
                  )}
                  {store.hasSelfDelivery && (
                    <Badge variant="outline">Self delivery</Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-sm">
              <Star className="h-4 w-4 fill-warning text-warning" />
              <span className="font-semibold">{store.rating.toFixed(1)}</span>
              <span className="text-muted-foreground">· {store.totalOrders} orders</span>
            </div>
          </div>
          {store.description && (
            <p className="mt-4 text-sm text-muted-foreground max-w-3xl">{store.description}</p>
          )}
        </CardContent>
      </Card>

      <StatsGrid columns={4}>
        <CompactStatCard title="Available BV" value={store.availableBv ?? 0} icon={Layers} />
        <CompactStatCard title="Total orders" value={dashboard?.totalOrders ?? store.totalOrders} icon={Package} />
        <CompactStatCard title="Products" value={dashboard?.totalProducts ?? "—"} icon={Package} />
        <CompactStatCard
          title="Revenue"
          value={dashboard ? `₹${dashboard.totalRevenue.toLocaleString()}` : "—"}
          icon={Wallet}
        />
      </StatsGrid>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" /> Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <DetailRow label="Phone" value={store.phone} />
            <DetailRow label="Email" value={store.email ?? "—"} />
            <DetailRow label="Owner username" value={store.ownerUsername ?? "—"} />
            <DetailRow label="Owner user ID" value={<span className="font-mono text-xs">{store.userId}</span>} />
            <DetailRow label="Referral code" value={store.referralCode} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" /> Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <DetailRow label="Address" value={store.fullAddress} />
            <DetailRow label="City / State" value={`${store.city}, ${store.state}`} />
            <DetailRow label="Pincode" value={store.pincode} />
            <DetailRow label="Coordinates" value={`${store.latitude}, ${store.longitude}`} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Truck className="h-4 w-4 text-primary" /> Delivery settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <DetailRow label="Delivery radius" value={`${store.deliveryRadius} km`} />
            <DetailRow label="Min order" value={`₹${store.minOrderAmount}`} />
            <DetailRow label="Delivery charge" value={`₹${store.deliveryCharge}`} />
            <DetailRow label="Free delivery above" value={`₹${store.freeDeliveryAbove}`} />
            <DetailRow label="Self delivery" value={store.hasSelfDelivery ? "Yes" : "No"} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" /> Operations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <DetailRow label="Opening hours" value={`${formatTime(store.openingTime)} – ${formatTime(store.closingTime)}`} />
            <DetailRow label="Today's orders" value={dashboard?.todayOrders ?? "—"} />
            <DetailRow label="Today's revenue" value={dashboard ? `₹${dashboard.todayRevenue.toLocaleString()}` : "—"} />
            <DetailRow label="Low stock products" value={dashboard?.lowStockProducts ?? "—"} />
            <DetailRow label="Available BV" value={store.availableBv ?? 0} />
            <DetailRow label="BV received / allocated" value={`${store.totalBvReceived ?? 0} / ${store.totalBvAllocated ?? 0}`} />
            <DetailRow label="Store ID" value={`#${store.id}`} />
          </CardContent>
        </Card>

        {/* KYC & Verification Details Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" /> KYC & Verification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
              <DetailRow label="KYC Status" value={
                store.kycVerified ? (
                  <Badge className="bg-success/10 text-success border-success/20">Verified</Badge>
                ) : (
                  <Badge className="bg-warning/10 text-warning border-warning/20">Pending Verification</Badge>
                )
              } />
              <DetailRow label="Aadhaar Number" value={store.aadharNumber ?? "—"} />
              <DetailRow label="PAN Number" value={store.panNumber ?? "—"} />
              <DetailRow label="GSTIN Number" value={store.gstNumber ?? "—"} />
            </div>

            {/* Document Images Grid */}
            <div className="py-4 border-t border-b border-border/60">
              <span className="text-sm font-semibold text-muted-foreground block mb-3">KYC Documents</span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground font-semibold uppercase block">Selfie</span>
                  {store.selfieUrl ? (
                    <a href={resolveUploadUrl(store.selfieUrl)} target="_blank" rel="noreferrer" className="block relative group overflow-hidden rounded-xl border aspect-square bg-muted">
                      <img src={resolveUploadUrl(store.selfieUrl)} alt="Selfie" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                        <span className="text-white text-xs font-medium">View Full</span>
                      </div>
                    </a>
                  ) : (
                    <div className="border border-dashed rounded-xl aspect-square flex items-center justify-center text-xs text-muted-foreground">No image</div>
                  )}
                </div>

                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground font-semibold uppercase block">Shop Certificate</span>
                  {store.shopCertificateUrl ? (
                    <a href={resolveUploadUrl(store.shopCertificateUrl)} target="_blank" rel="noreferrer" className="block relative group overflow-hidden rounded-xl border aspect-square bg-muted">
                      <img src={resolveUploadUrl(store.shopCertificateUrl)} alt="Certificate" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                        <span className="text-white text-xs font-medium">View Full</span>
                      </div>
                    </a>
                  ) : (
                    <div className="border border-dashed rounded-xl aspect-square flex items-center justify-center text-xs text-muted-foreground">No image</div>
                  )}
                </div>

                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground font-semibold uppercase block">Aadhaar Card</span>
                  {store.aadharCardUrl ? (
                    <a href={resolveUploadUrl(store.aadharCardUrl)} target="_blank" rel="noreferrer" className="block relative group overflow-hidden rounded-xl border aspect-square bg-muted">
                      <img src={resolveUploadUrl(store.aadharCardUrl)} alt="Aadhaar" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                        <span className="text-white text-xs font-medium">View Full</span>
                      </div>
                    </a>
                  ) : (
                    <div className="border border-dashed rounded-xl aspect-square flex items-center justify-center text-xs text-muted-foreground">No image</div>
                  )}
                </div>

                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground font-semibold uppercase block">GST Certificate</span>
                  {store.gstCertificateUrl ? (
                    <a href={resolveUploadUrl(store.gstCertificateUrl)} target="_blank" rel="noreferrer" className="block relative group overflow-hidden rounded-xl border aspect-square bg-muted">
                      <img src={resolveUploadUrl(store.gstCertificateUrl)} alt="GST" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                        <span className="text-white text-xs font-medium">View Full</span>
                      </div>
                    </a>
                  ) : (
                    <div className="border border-dashed rounded-xl aspect-square flex items-center justify-center text-xs text-muted-foreground">No image</div>
                  )}
                </div>
              </div>
            </div>

            {/* Bank details */}
            <div className="pt-2">
              <span className="text-sm font-semibold text-muted-foreground block mb-2">Bank Account Details</span>
              <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
                <DetailRow label="Bank Name" value={store.bankName ?? "—"} />
                <DetailRow label="Account Name" value={store.bankAccountName ?? "—"} />
                <DetailRow label="Account Number" value={store.bankAccountNumber ?? "—"} />
                <DetailRow label="IFSC Code" value={store.bankIfscCode ?? "—"} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {products && products.items.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Recent catalog</CardTitle>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/products`}>View all products</Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              {products.items.slice(0, 5).map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <StandardImage src={p.primaryImage} alt={p.name} standard="product" className="h-10 w-10" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.categoryName}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-medium">₹{p.sellingPrice}</p>
                    <p className="text-xs text-muted-foreground">{p.statusText}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {store.storeName}?</AlertDialogTitle>
            <AlertDialogDescription>This soft-deletes the store from the platform.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AdminAdjustStoreBvDialog
        open={bvOpen}
        onOpenChange={setBvOpen}
        storeId={storeId}
        storeName={store.storeName}
      />
    </div>
  );
};

export default AdminStoreDetail;
